import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  UserGroupIcon, 
  BriefcaseIcon, 
  ChatBubbleLeftRightIcon,
  CogIcon,
  DocumentTextIcon,
  PhotoIcon,
  PresentationChartBarIcon,
  BuildingOffice2Icon,
  ArrowLeftOnRectangleIcon,
  RectangleStackIcon,
  StarIcon,
  UsersIcon,
  EnvelopeIcon,
  CommandLineIcon,
  ComputerDesktopIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const AdminSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState(['overview', 'content', 'engagement', 'recruitment', 'communications', 'system']);

  const menuCategories = [
    {
      name: 'Overview',
      key: 'overview',
      items: [
        { name: 'Dashboard', href: '/admin', icon: HomeIcon },
        { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon },
      ]
    },
    {
      name: 'Content Management',
      key: 'content',
      items: [
        { name: 'Company Info', href: '/admin/company', icon: BuildingOffice2Icon },
        { name: 'Media Library', href: '/admin/media', icon: PhotoIcon },
        { name: 'Services', href: '/admin/services', icon: CogIcon },
        { name: 'Products', href: '/admin/products', icon: PresentationChartBarIcon },
        { name: 'Portfolio', href: '/admin/portfolio', icon: RectangleStackIcon },
        { name: 'Technologies', href: '/admin/technologies', icon: CommandLineIcon },
        { name: 'Team Members', href: '/admin/team', icon: UserGroupIcon },
        { name: 'Testimonials', href: '/admin/testimonials', icon: StarIcon },
      ]
    },
    {
      name: 'Engagement',
      key: 'engagement',
      items: [
        { name: 'Blog Posts', href: '/admin/blog', icon: DocumentTextIcon },
        { name: 'Chatbot', href: '/admin/chat', icon: ChatBubbleLeftRightIcon },
        { name: 'Messages', href: '/admin/messages', icon: EnvelopeIcon },
        { name: 'Newsletter', href: '/admin/newsletter', icon: EnvelopeIcon },
      ]
    },
    {
      name: 'Recruitment',
      key: 'recruitment',
      items: [
        { name: 'Job Postings', href: '/admin/jobs', icon: BriefcaseIcon },
        { name: 'Job Applications', href: '/admin/job-applications', icon: ClipboardDocumentListIcon },
        { name: 'Interview Calendar', href: '/admin/interviews', icon: CalendarIcon },
        { name: 'Pipeline', href: '/admin/pipeline', icon: RectangleStackIcon },
        { name: 'Recruitment Analytics', href: '/admin/recruitment-analytics', icon: ChartBarIcon },
      ]
    },
    {
      name: 'Communications',
      key: 'communications',
      items: [
        { name: 'Email Configuration', href: '/admin/email-config', icon: CogIcon },
        { name: 'Email Templates', href: '/admin/email-templates', icon: DocumentTextIcon },
        { name: 'Chatbot Responses', href: '/admin/chatbot-responses', icon: ChatBubbleLeftRightIcon },
        { name: 'Communication Logs', href: '/admin/communication-logs', icon: ClipboardDocumentListIcon },
        { name: 'Notifications', href: '/admin/notifications-management', icon: EnvelopeIcon },
      ]
    },
    {
      name: 'System',
      key: 'system',
      items: [
        { name: 'Users', href: '/admin/users', icon: UsersIcon },
        { name: 'Sessions', href: '/admin/sessions', icon: ComputerDesktopIcon },
        { name: 'Audit Logs', href: '/admin/audit-logs', icon: ClipboardDocumentListIcon },
        { name: 'Database Backup', href: '/admin/database-backup', icon: RectangleStackIcon },
      ]
    }
  ];

  const toggleCategory = (key) => {
    setExpandedCategories(prev =>
      prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key]
    );
  };

  const handleLogout = () => {
    // Add logout logic here
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity lg:hidden z-20"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-gray-900 transform transition-transform duration-300 ease-in-out flex flex-col
        lg:translate-x-0 lg:static lg:inset-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-center h-16 bg-gray-800 flex-shrink-0">
          <h1 className="text-white text-xl font-bold">LionTek Admin</h1>
        </div>

        <nav className="flex-1 mt-5 px-2 overflow-y-auto pb-4">
          <div className="space-y-4">
            {menuCategories.map((category) => {
              const isExpanded = expandedCategories.includes(category.key);
              return (
                <div key={category.key}>
                  {/* Category Header */}
                  <button
                    onClick={() => toggleCategory(category.key)}
                    className="flex items-center justify-between w-full px-2 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-300 transition-colors"
                  >
                    <span>{category.name}</span>
                    {isExpanded ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>

                  {/* Category Items */}
                  {isExpanded && (
                    <div className="mt-1 space-y-1">
                      {category.items.map((item) => {
                        const isActive = location.pathname === item.href;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`
                              group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                              ${isActive
                                ? 'bg-gray-800 text-white'
                                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                              }
                            `}
                            onClick={() => onClose && onClose()}
                          >
                            <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Logout button */}
          <div className="mt-8 pt-8 border-t border-gray-700">
            <button
              onClick={handleLogout}
              className="group flex items-center w-full px-2 py-2 text-sm font-medium text-gray-300 rounded-md hover:bg-gray-700 hover:text-white transition-colors"
            >
              <ArrowLeftOnRectangleIcon className="mr-3 h-6 w-6 flex-shrink-0" />
              Logout
            </button>
          </div>
        </nav>

        {/* User info */}
        <div className="flex-shrink-0 w-full p-4 bg-gray-800 border-t border-gray-700">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-gray-600 flex items-center justify-center">
                <span className="text-sm font-medium text-white">A</span>
              </div>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-white">Admin User</p>
              <p className="text-xs text-gray-400">admin@liontek.com</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;